/* eslint-disable no-restricted-syntax */
// https://stackoverflow.com/a/53182678

import { XMLParser } from 'fast-xml-parser';

import { activeLanes } from '../../constants';
import { EventTypeIdentifier, EventTypePalette } from '../../enums';
import { DecodedKML, Folder, KMLEntry, ParsedRouteDefinition, Placemark, RootFolder } from '../../interfaces';

export const parseLocalKMLFile = (file: any): Promise<ParsedRouteDefinition> => {
  const fileReader = new FileReader();

  return new Promise((resolve, reject) => {
    fileReader.onload = async (e: any) => {
      const result = extractGoogleCoords(e.target.result);
      resolve(result);
    };
    fileReader.readAsText(file);
  });
};

export const extractGoogleCoords = (plainText: string): ParsedRouteDefinition => {
  const googlePolygons: ParsedRouteDefinition['polygons'] = [];
  const googleMarkers: ParsedRouteDefinition['markers'] = [];

  // Helper function to check if a placemark should be ignored
  const shouldIgnorePlacemark = (name: string): boolean => {
    // Check if the name is any of R0-R10
    return /^R([0-9]|10)$/.test(name);
  };

  const addLine = (blockName: string, name: string, coordinates: string, block: any) => {
    // Skip processing if this is a lane line marker
    if (shouldIgnorePlacemark(name)) {
      return;
    }

    const [eventTypeAndLocation = '', enforceTimeAndDays = '', activeLane = ''] = name.split('|');
    const enforceString = enforceTimeAndDays.replace?.('[', '').replace?.(']', '') || '';
    const enforceDays = enforceString.split(',').pop() || 'ALL';
    const enforceTime = enforceString.split(',').slice(0, -1).join(','); // in case time contains a comma

    if (!coordinates) {
      return;
    }

    // #SB|Wyckoff Ave @ Cornelia St
    const route_direction = block.Placemark?.name?.split('|')[0].replace('#', '');
    const blockNameIntersection = block.Placemark?.name?.split('|')[1] || blockName;

    const regexpWithOptionalLocationID = /(\w+)(?:\[#(\d+)\])?/;
    const query2 = eventTypeAndLocation.match(regexpWithOptionalLocationID);
    const eventType = query2?.[1] ?? '';
    const bus_stop_id = query2?.[2]; // optional matching group

    // split string by single character or comma
    const enforce_days =
      enforceDays === 'ALL' ? [0, 1, 2, 3, 4, 5, 6] : enforceDays.replaceAll(',', '').split('').map(Number);
    const enforcement_times = enforceTime.split(',').map((v) => Number(v.replace(',', '.')));
    const enforcement_info: KMLEntry['enforcement_info'] = [];
    const active_lane_selection_acronyms = activeLane.split(',');
    const active_lane_selection = activeLanes
      .filter((lane) => active_lane_selection_acronyms.includes(lane.acronym))
      .map((l) => l.id);

    const eventTypeMapping = {
      BusStop: EventTypeIdentifier.BUS_STOP,
      BusStopParking: EventTypeIdentifier.BUS_STOP_PARKING,
      DoubleParked: EventTypeIdentifier.DOUBLE_PARKED,
      BusLane: EventTypeIdentifier.BUS_LANE,
      BusLaneMoving: EventTypeIdentifier.BUS_LANE_MOVING,
      BusLaneParking: EventTypeIdentifier.BUS_LANE_PARKING,
      BikeLane: EventTypeIdentifier.BIKE_LANE,
      DoubleYellow: EventTypeIdentifier.DOUBLE_YELLOW,
      NoStoppingTest: EventTypeIdentifier.NO_STOPPING_TEST,
      NoStopping: EventTypeIdentifier.NO_STOPPING,
      StreetcarBlocked: EventTypeIdentifier.STREETCAR_BLOCKED,
      StreetcarStop: EventTypeIdentifier.STREETCAR_STOP,
    };

    const mapEventType = (kmlEventType: string): EventTypeIdentifier => {
      return eventTypeMapping[kmlEventType] ?? EventTypeIdentifier.BUS_LANE;
    };

    const eventTypeColors = (et: EventTypeIdentifier): string => {
      return EventTypePalette[et] ?? '#FF00FF';
    };

    const event_types = eventType.split(',').map(mapEventType);

    for (let i = 0; i < enforcement_times.length; i += 2) {
      const enforce_start = enforcement_times[i];
      const enforce_end = enforcement_times[i + 1];
      enforcement_info.push({
        id: Math.random(),
        event_types,
        enforce_start,
        enforce_end,
        enforce_days,
        active_lane_selection,
        block_name: blockNameIntersection,
      });
    }

    const data: Partial<KMLEntry> = {
      event_types,
      bus_stop_id,
      active_lane_selection,
      enforcement_info,
      block_name: blockNameIntersection,
      route_direction,
    };

    const points = coordinates.split(' ');

    const googlePolygonsPaths: ParsedRouteDefinition['polygons'][0]['path'] = [];
    for (const point of points) {
      const coord = point.split(',');
      googlePolygonsPaths.push({ lat: +coord[1], lng: +coord[0] });
    }

    googlePolygons.push({
      path: googlePolygonsPaths,
      color: eventTypeColors(data.event_types[0]),
      data,
      type: data.event_types[0],
    });
  };

  const parseBlock = (block: Folder | RootFolder) => {
    let placemark: Placemark | Placemark[] | undefined;

    if (Array.isArray(block.Folder)) {
      const enforcementZone = block.Folder.find((f) => f.name === 'EnforceZones');
      placemark = enforcementZone?.Placemark;
    } else if (block.Folder?.Placemark) {
      placemark = block.Folder?.Placemark;
    } else {
      placemark = block.Placemark;
    }

    if (Array.isArray(placemark)) {
      placemark.forEach((p) => {
        if (!shouldIgnorePlacemark(p.name)) {
          addLine(block.name, p.name, p.LineString?.coordinates, block);
        }
      });
    } else if (placemark && !shouldIgnorePlacemark(placemark.name)) {
      addLine(block.name, placemark.name, placemark.LineString?.coordinates, block);
    }
  };

  // Recursively process all folders to find and process leaf nodes
  const processFolder = (folder: Folder | RootFolder) => {
    // Skip processing if this is a LaneLines folder
    if (folder.name === 'LaneLines') {
      return;
    }

    // Process this folder if it has placemarks
    if (folder.Placemark) {
      parseBlock(folder);
    } else if (folder.Folder) {
      // Continue traversing if there are nested folders
      if (Array.isArray(folder.Folder)) {
        folder.Folder.forEach(processFolder);
      } else {
        processFolder(folder.Folder);
      }
    }
  };

  const xmlparser = new XMLParser();
  const jObj: DecodedKML = xmlparser.parse(plainText);

  // support for old file format
  if (jObj.kml.Document?.Placemark) {
    parseBlock(jObj.kml.Document);
  }

  // support for new file format with recursive folder traversal
  if (jObj.kml.Document?.Folder) {
    if (Array.isArray(jObj.kml.Document.Folder)) {
      jObj.kml.Document.Folder.forEach(processFolder);
    } else {
      processFolder(jObj.kml.Document.Folder);
    }
  }

  return { markers: googleMarkers, polygons: googlePolygons };
};
